import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { validateSocialSecurityNumber, validateYears } from '@app/core/misc/validation-helper';
import { CollaborationPackageSelectors } from '@app/core/state/collaboration-packages/collaboration-packages.selectors';
import {
  ActionStrategy,
  ActionStrategyParams,
  ActionType,
  AppModule,
  ClientActionType,
  Data,
  Result,
} from '@app/core/state/types/collaboration-package.types';
import { BlFrontendButtonComponent } from '@app/core/wrappers/bl-frontend-button.component';
import { BlFrontendRadiobuttonsComponent } from '@app/core/wrappers/bl-frontend-radiobuttons.component';
import { validateEmail } from '@app/shared/misc/email-validator';
import { RadioButtonProps } from '@bl/components';
import { TranslocoModule } from '@jsverse/transloco';
import { CheckboxModule } from 'primeng/checkbox';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { BehaviorSubject, filter, switchMap, take } from 'rxjs';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableFormDialogBaseComponent } from 'src/app/core/i18n/translatable-form-dialog-base.component';
import { FirmSelectors } from 'src/app/core/state/firm/firm.selectors';
import { BlFrontendEmailInputComponent } from 'src/app/core/wrappers/bl-frontend-email-input.component';
import { BlFrontendTextInputComponent } from 'src/app/core/wrappers/bl-frontend-text-input.component';
import { BlFrontendTooltipComponent } from 'src/app/core/wrappers/bl-frontend-tooltip.component';
import { SafeHtmlPipe } from 'src/app/shared/pipes/safe-html.pipe';
import { BlFrontendAlertComponent } from '../../../core/wrappers/bl-frontend-alert.component';
import { createStrategyFactory } from './action-strategy-factory';
import { archiveWarningMessage, showArchiveWarningIfRecentlyUsed } from './archive-database-helper';
import { DataLoaderService } from './data-loader.service';
import { initializeActionRadioButtons, initializeClientActionRadioButtons } from './radio-buttons-helper';
import { ActionSelectorDialogComponentTranslations } from './types';

const TRANSLATION_SCOPE = 'actionSelectorDialogComponent';

@Component({
  selector: 'app-action-selector-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './action-selector-dialog.component.html',
  providers: [getTranslationProvider(TRANSLATION_SCOPE), DataLoaderService],
  imports: [
    CommonModule,
    FormsModule,
    BlFrontendRadiobuttonsComponent,
    BlFrontendButtonComponent,
    InputTextModule,
    CheckboxModule,
    BlFrontendAlertComponent,
    SafeHtmlPipe,
    TranslocoModule,
    BlFrontendTextInputComponent,
    BlFrontendEmailInputComponent,
    BlFrontendTooltipComponent,
  ],
})
export class ActionSelectorDialogComponent extends TranslatableFormDialogBaseComponent<Data, Result> {
  radioButtonsItems: RadioButtonProps[];
  clientActionRadioButtonsItems: RadioButtonProps[];
  email: BehaviorSubject<string> = new BehaviorSubject<string>('');
  years: BehaviorSubject<number> = new BehaviorSubject<number>(7);
  socialSecurityNumber: BehaviorSubject<string> = new BehaviorSubject<string>('');
  clientAction: BehaviorSubject<ClientActionType> = new BehaviorSubject<ClientActionType>(ClientActionType.SKIP);
  confirmArchive = false;
  confirmDelete = false;
  confirmUnarchive = false;

  private strategyFactory = createStrategyFactory();
  private currentStrategy: ActionStrategy = this.initializeStrategy();
  private isMiniBundle = (modules: AppModule[]) => modules?.some((module) => module.id === 'appmini');

  get companyName(): string {
    return this.config.data.name;
  }

  get existsInFirmSupport(): boolean {
    return this.config.data.existsInFirmSupport;
  }

  get isLoadingSelected(): boolean {
    return this.currentStrategy?.type === ActionType.LOADING;
  }

  get isOnboardSelected(): boolean {
    return this.currentStrategy?.type === ActionType.ONBOARD;
  }

  get isOnboardAsAnnualClientSelected(): boolean {
    return this.currentStrategy?.type === ActionType.ONBOARD_WITH_ANNUAL_CLIENT;
  }

  get isArchivedSelected(): boolean {
    return this.currentStrategy?.type === ActionType.ARCHIVE;
  }

  get isUnarchiveSelected(): boolean {
    return this.currentStrategy?.type === ActionType.UNARCHIVE;
  }

  get isDeleteSelected(): boolean {
    return this.currentStrategy?.type === ActionType.DELETE;
  }

  get primaryButtonI18nKey(): string {
    return this.currentStrategy?.primaryButtonI18nKey;
  }

  get showArchiveWarningIfRecentlyUsed(): boolean {
    return showArchiveWarningIfRecentlyUsed(this.config.data.lastLogin);
  }

  get archiveWarningText(): string {
    return archiveWarningMessage(this.translate, this.config.data.lastLogin);
  }

  get showMiniToAnnualClientInformation(): boolean {
    const hasAnnualClientOption = this.radioButtonsItems?.some(
      (item) => item.value === ActionType.ONBOARD_WITH_ANNUAL_CLIENT,
    );
    return this.config.data.canChangeToAnnualClient && hasAnnualClientOption && !this.isLoadingSelected;
  }

  get showMiniUpgradeInformation(): boolean {
    const hasAnnualClientOption = this.radioButtonsItems?.some(
      (item) => item.value === ActionType.ONBOARD_WITH_ANNUAL_CLIENT,
    );
    return (
      this.isMiniBundle(this.config.data.subscription.modules) && !hasAnnualClientOption && !this.isLoadingSelected
    );
  }

  get isValid(): boolean {
    const validationMap = {
      [ActionType.ONBOARD]: () => validateEmail(this.email.value),
      [ActionType.ONBOARD_WITH_ANNUAL_CLIENT]: () => validateSocialSecurityNumber(this.socialSecurityNumber.value),
      [ActionType.CHANGE_PACKAGE_ON_NEW_SUBSCRIPTION]: () => true,
      [ActionType.ARCHIVE]: () => validateYears(this.years.value) && this.confirmArchive,
      [ActionType.UNARCHIVE]: () =>
        validateSocialSecurityNumber(this.socialSecurityNumber.value) && this.confirmUnarchive,
      [ActionType.DELETE]: () => this.confirmDelete,
    };

    return validationMap[this.currentStrategy?.type]?.() ?? false;
  }

  // eslint-disable-next-line max-params
  constructor(
    dynamicDialogRef: DynamicDialogRef,
    dynamicDialogConfig: DynamicDialogConfig<Data>,
    private changeDetectorRef: ChangeDetectorRef,
    private dataLoaderService: DataLoaderService,
  ) {
    super(dynamicDialogRef, dynamicDialogConfig);
  }

  protected static override getTranslationScope(): string {
    return TRANSLATION_SCOPE;
  }

  protected static override getDialogConfig(
    translation: ActionSelectorDialogComponentTranslations,
  ): Omit<DynamicDialogConfig<Data>, 'data'> {
    return {
      header: translation.actionSelectorHeadline,
      modal: true,
      dismissableMask: true,
      draggable: false,
      width: '530px',
      style: { maxHeight: '90vh' },
      styleClass: 'action-selector-dialog tabbed-dialog',
    };
  }

  protected override onInitialized: () => void = () => {
    this.store
      .select(FirmSelectors.selectCountryCode)
      .pipe(
        switchMap((countryCode) =>
          initializeActionRadioButtons({
            config: this.config.data,
            market: countryCode,
            translateFn: this.translate,
            dataLoaderService: this.dataLoaderService,
          }),
        ),
        take(1),
      )
      .subscribe((radioButtons) => {
        // initialize the radio buttons and gets the default strategy
        this.radioButtonsItems = radioButtons;
        this.clientActionRadioButtonsItems = initializeClientActionRadioButtons(false, this.translate);
        this.currentStrategy = this.initializeStrategy();
        this.changeDetectorRef.markForCheck();
      });

    this.listenForEmailChangesInStore();
    this.listenForClientChangesInStore();
  };

  actionChanged(value: string): void {
    this.confirmArchive = false;
    this.confirmDelete = false;
    this.confirmUnarchive = false;

    // update the current strategy from the selected radio button value
    this.currentStrategy = this.strategyFactory(value as ActionType);
  }

  clientActionChanged(value: string): void {
    this.clientAction.next(value as ClientActionType);
  }

  next(): void {
    const config: ActionStrategyParams = {
      cloudApiKey: this.config.data.cloudApiKey,
      clientId: this.config.data.clientId,
      name: this.config.data.name,
      email: this.email.value,
      years: this.years.value,
      socialSecurityNumber: this.socialSecurityNumber.value,
      clientAction: this.clientAction.value,
      subscription: this.config.data.subscription,
    };

    this.currentStrategy.execute(config);
    this.close({ result: this.currentStrategy.type });
  }

  private initializeStrategy(): ActionStrategy {
    const selectedActionType =
      (this.radioButtonsItems?.find((item) => item.checked)?.value as ActionType) ?? ActionType.LOADING;
    return this.strategyFactory(selectedActionType);
  }

  private listenForEmailChangesInStore(): void {
    this.store
      .select(CollaborationPackageSelectors.selectEmail)
      .pipe(this.takeUntilDestroyed())
      .subscribe({ next: (value) => this.email.next(value) });
  }

  private listenForClientChangesInStore(): void {
    this.store
      .select(CollaborationPackageSelectors.selectClient)
      .pipe(
        filter((client) => !!client),
        this.takeUntilDestroyed(),
      )
      .subscribe({
        next: (client) => {
          this.clientActionRadioButtonsItems = initializeClientActionRadioButtons(
            Boolean(client?.deletable),
            this.translate,
          );
          this.changeDetectorRef.markForCheck();
        },
      });
  }
}
